import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SyncDashboard from './components/SyncDashboard';
import ConflictResolution from './components/ConflictResolution';
import SystemIntegration from './components/SystemIntegration';
import SyncAnalytics from './components/SyncAnalytics';

const RealTimeInventorySynchronizationCenter = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, isStaff, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [syncStatus, setSyncStatus] = useState({
    connected: true,
    lastSync: new Date(),
    pendingUpdates: 0,
    syncHealth: 'healthy'
  });
  const [realtimeChannel, setRealtimeChannel] = useState(null);

  useEffect(() => {
    if (!authLoading && (!user || (!isGymOwner && !isStaff))) {
      navigate('/member-dashboard');
    }
  }, [user, isGymOwner, isStaff, authLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id && (isGymOwner || isStaff)) {
      setupRealtimeSync();
    }

    return () => {
      if (realtimeChannel) {
        supabase?.removeChannel(realtimeChannel);
      }
    };
  }, [currentTenant, isGymOwner, isStaff]);

  const setupRealtimeSync = () => {
    const channel = supabase
      ?.channel('inventory-sync-center')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'pos_products',
          filter: `tenant_id=eq.${currentTenant?.id}`
        },
        (payload) => {
          handleInventoryChange(payload);
        }
      )
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'pos_inventory_logs',
          filter: `tenant_id=eq.${currentTenant?.id}`
        },
        (payload) => {
          handleInventoryLogChange(payload);
        }
      )
      ?.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          setSyncStatus(prev => ({
            ...prev,
            connected: true,
            syncHealth: 'healthy',
            lastSync: new Date()
          }));
        } else if (status === 'CHANNEL_ERROR') {
          setSyncStatus(prev => ({
            ...prev,
            connected: false,
            syncHealth: 'error'
          }));
        }
      });

    setRealtimeChannel(channel);
  };

  const handleInventoryChange = (payload) => {
    setSyncStatus(prev => ({
      ...prev,
      lastSync: new Date(),
      pendingUpdates: Math.max(0, prev?.pendingUpdates - 1)
    }));
  };

  const handleInventoryLogChange = (payload) => {
    setSyncStatus(prev => ({
      ...prev,
      lastSync: new Date()
    }));
  };

  const handleManualSync = async () => {
    setSyncStatus(prev => ({ ...prev, syncHealth: 'syncing' }));
    
    // Simulate manual sync operation
    setTimeout(() => {
      setSyncStatus(prev => ({
        ...prev,
        syncHealth: 'healthy',
        lastSync: new Date(),
        pendingUpdates: 0
      }));
    }, 2000);
  };

  const tabs = [
    { id: 'dashboard', label: 'Sync Dashboard', icon: 'Activity' },
    { id: 'conflicts', label: 'Conflict Resolution', icon: 'AlertTriangle' },
    { id: 'systems', label: 'System Integration', icon: 'Network' },
    { id: 'analytics', label: 'Sync Analytics', icon: 'BarChart3' }
  ];

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading synchronization center...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-6 pb-24 lg:pb-6">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl font-bold text-foreground">Real-Time Inventory Synchronization</h1>
            <div className="flex items-center gap-3">
              {/* Sync Status Indicator */}
              <div className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg">
                <Icon
                  name={syncStatus?.connected ? 'Wifi' : 'WifiOff'}
                  size={18}
                  className={syncStatus?.connected ? 'text-success' : 'text-danger'}
                />
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-foreground">
                    {syncStatus?.connected ? 'Connected' : 'Disconnected'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {syncStatus?.lastSync?.toLocaleTimeString()}
                  </span>
                </div>
              </div>
              <Button
                onClick={handleManualSync}
                disabled={syncStatus?.syncHealth === 'syncing'}
              >
                <Icon
                  name="RefreshCw"
                  size={18}
                  className={`mr-2 ${syncStatus?.syncHealth === 'syncing' ? 'animate-spin' : ''}`}
                />
                Manual Sync
              </Button>
            </div>
          </div>
          <p className="text-muted-foreground">
            Automated synchronization protocols ensuring accurate stock levels across all systems
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-border mb-6">
          <div className="flex gap-1 overflow-x-auto">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={18} />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'dashboard' && (
            <SyncDashboard
              tenantId={currentTenant?.id}
              syncStatus={syncStatus}
              onManualSync={handleManualSync}
            />
          )}
          {activeTab === 'conflicts' && (
            <ConflictResolution
              tenantId={currentTenant?.id}
            />
          )}
          {activeTab === 'systems' && (
            <SystemIntegration
              tenantId={currentTenant?.id}
              syncStatus={syncStatus}
            />
          )}
          {activeTab === 'analytics' && (
            <SyncAnalytics
              tenantId={currentTenant?.id}
            />
          )}
        </div>
      </main>
      <MobileBottomNav />
    </div>
  );
};

export default RealTimeInventorySynchronizationCenter;